import { useState, useEffect } from 'react';
import { runAIMatches, runMultiMatches, runMatchesAnalyze, saveIcp } from '../utils/api';
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue';

/**
 * Custom hook for managing match results
 * @param {Object} options - Options for the hook
 * @param {Array} options.icpRequirements - Centralized ICP requirements
 * @returns {Object} Match results state and functions
 */
const useMatchResults = (options = {}) => {
  const { icpRequirements } = options;
  const [matchResults, setMatchResults] = useState(null);
  const [matchCount, setMatchCount] = useState(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesAnalyze, setMatchesAnalyze] = useState(null);
  const [sessionSearchId, setSessionSearchId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Simplified refresh match results based on central queue items
   * @param {Array} queueItems - Central discovery queue items with disco_done = true
   * @returns {Promise} A promise that resolves when all operations are complete
   */
  const handleRefreshResultsFromQueue = async (queueItems) => {
    return new Promise(async (resolve, reject) => {
      console.log('🔍 handleRefreshResultsFromQueue CALLED with queue items:', queueItems?.length || 0);
      // Skip queue-driven refresh while JD intake is in-flight
      if (typeof window !== 'undefined' && window.__jdInFlight === true) {
        console.log('⏳ JD in-flight: suppressing queue-derived refresh');
        resolve();
        return;
      }

      if (!queueItems || queueItems.length === 0) {
        console.log('🔍 No queue items provided, skipping refresh');
        resolve();
        return;
      }

      // Filter only completed queue items (disco_done = true)
      const completedItems = queueItems.filter(item => item.disco_done === true);
      
      if (completedItems.length === 0) {
        console.log('🔍 No completed queue items found, skipping refresh');
        resolve();
        return;
      }

      console.log('🔍 Found completed queue items:', completedItems.map(item => ({
        section_id: item.section_id,
        section_name: item.section_name,
        section_type: item.section_type,
        bool_str: item.bool_str
      })));

      setMatchesLoading(true);
      setMatchResults(null);
      setMatchCount(null);
      setMatchesAnalyze(null);

      try {
        // Construct ICP requirements from completed queue items using only the 4 required fields
        const icpRequirements = completedItems.map(item => ({
          section_type: item.section_type || 'Must Have',
          section_name: item.section_name,
          section_id: item.section_id,
          bool_str: item.bool_str || ''
        }));

        console.log('🔍 Constructed ICP requirements from queue:', icpRequirements);

        // Create mock results structure to maintain compatibility
        const results = {
          search_gen_response: {
            icp_requirements: icpRequirements
          }
        };
        
        setMatchResults(results);
        setMatchesLoading(false);

        const to_match = [
          'cmetrics-female',
          'cmetrics-male',
          'cmetrics-african american',
          'cmetrics-south asian',
          'cmetrics-middle eastern',
          'cmetrics-caucasian',
          'cmetrics-east asian',
          'cmetrics-caucasian',
          'cmetrics-hispanic'
        ];
        
        try {
          // Run Multi Matches using queue-derived ICP requirements
          const multiMatchResults = await runMultiMatches(
            icpRequirements, // ✅ Use queue-derived requirements
            to_match,
            []
          );
          setMatchCount(multiMatchResults);
          
          // If matchCount is greater than 10, run matches analyze with current org option
          let opt = {run_on_self_org: false}
          if (multiMatchResults && Object.values(multiMatchResults).some(count => count > 10)) {
            opt.run_on_self_org = true;
          }
          try {
            // Run matches analyze using queue-derived ICP requirements
            const analyzeResults = await runMatchesAnalyze(icpRequirements, opt); // ✅ Use queue-derived requirements
            setMatchesAnalyze(analyzeResults);
            resolve(); // Resolve the promise when everything is done
          } catch (analyzeErr) {
            console.error('Error running matches analyze:', analyzeErr);
            setMatchesAnalyze(null);
            resolve(); // Still resolve to ensure .finally() is called
          }
        } catch (err) {
          console.error('Error fetching multi-match results:', err);
          setMatchCount(null); // Reset in case of error
          resolve(); // Still resolve to ensure .finally() is called
        }
      } catch (err) {
        console.error('Error in handleRefreshResultsFromQueue:', err);
        setMatchResults(null); // Reset in case of error
        setMatchesLoading(false);
        resolve(); // Still resolve to ensure .finally() is called
      }
    });
  };

  // Auto-update match count when completed queue items change
  useEffect(() => {
    console.log('🔄 AUTO-UPDATE: Setting up queue listener in useMatchResults');
    
    const updateMatchCountFromQueue = (queueItems) => {
      // Do not propagate queue-driven updates while JD intake is in-flight
      if (typeof window !== 'undefined' && window.__jdInFlight === true) {
        console.log('⏳ JD in-flight: skipping auto-update from queue');
        return;
      }
      console.log('🔄 AUTO-UPDATE: Queue changed, received', queueItems?.length || 0, 'total items');
      
      const completedItems = queueItems.filter(item => item.disco_done === true);
      console.log('🔄 AUTO-UPDATE: Found', completedItems.length, 'completed items');
      
      if (completedItems.length > 0) {
        console.log('🔄 AUTO-UPDATE: Completed items:', completedItems.map(item => ({
          section_id: item.section_id,
          section_name: item.section_name,
          disco_done: item.disco_done
        })));
        
        console.log('🔄 AUTO-UPDATE: Calling handleRefreshResultsFromQueue...');
        handleRefreshResultsFromQueue(completedItems).catch(error => {
          console.error('❌ AUTO-UPDATE: Error updating match count:', error);
        });
      } else {
        console.log('🔄 AUTO-UPDATE: No completed items, clearing match count');
        setMatchCount(null);
      }
    };

    // Listen for queue changes
    centralDiscoveryQueue.addListener(updateMatchCountFromQueue);
    console.log('🔄 AUTO-UPDATE: Queue listener added');
    
    // Initial update
    const initialItems = centralDiscoveryQueue.getAllItems();
    console.log('🔄 AUTO-UPDATE: Initial update with', initialItems.length, 'items');
    updateMatchCountFromQueue(initialItems);

    // Cleanup listener on unmount
    return () => {
      console.log('🔄 AUTO-UPDATE: Cleaning up queue listener');
      centralDiscoveryQueue.removeListener(updateMatchCountFromQueue);
    };
  }, []); // Empty dependency array since handleRefreshResultsFromQueue is stable

  /**
   * Refresh match results based on messages
   * @param {Array} messages - Chat messages
   * @param {Object} preloadedResults - Optional preloaded results (used for experience histogram)
   * @returns {Promise} A promise that resolves when all operations are complete
   */
  const handleRefreshResults = async (messages, preloadedResults, aiOptions = {}) => {
    return new Promise(async (resolve, reject) => {
      console.log('🔍 handleRefreshResults CALLED with:', {
        messagesLength: messages?.length || 0,
        hasPreloadedResults: !!preloadedResults,
        firstFewMessages: messages?.slice(0, 3)?.map(m => ({ role: m.role, content: m.content?.substring(0, 50) + '...' }))
      });
      // Gate UI/queue while JD intake is in flight
      if (aiOptions?.intakeType === 'jd' && typeof window !== 'undefined') {
        window.__jdInFlight = true;
      }
      
      setMatchesLoading(true);
      setMatchResults(null);
      setMatchCount(null);
      setMatchesAnalyze(null);
  
      try {
        // If preloadedResults is provided, use it instead of calling runAIMatches
        let results;
        if (preloadedResults) {
          console.log('🔍 Using preloaded results:', preloadedResults);
          results = preloadedResults;
          // Clear JD in-flight if we were gated but using preloaded results
          if (aiOptions?.intakeType === 'jd' && typeof window !== 'undefined') {
            window.__jdInFlight = false;
          }
        } else {
          console.log('🔍 Calling runAIMatches with messages:', messages?.length, 'aiOptions:', aiOptions);
          // Run AI Matches (routes to JD SearchGen when aiOptions.intakeType === 'jd')
          results = await runAIMatches(messages, aiOptions);
          // Clear JD in-flight gate as soon as JD results return
          if (aiOptions?.intakeType === 'jd' && typeof window !== 'undefined') {
            window.__jdInFlight = false;
          }
          console.log('🔍 runAIMatches returned:', results);
          
          // If this came from JD flow, seed the Central Discovery Queue with all icp_requirements as pending
          try {
            if (aiOptions?.intakeType === 'jd' && results?.search_gen_response?.icp_requirements?.length) {
              // Idempotency guard: only seed JD requirements once per session
              let alreadySeeded = false;
              try {
                const existing = typeof centralDiscoveryQueue?.getAllItems === 'function'
                  ? centralDiscoveryQueue.getAllItems()
                  : [];
                alreadySeeded = existing.some(it => it?._seededFrom === 'jd');
                console.log('📥 JD seeding check:', { existingCount: existing.length, alreadySeeded });
              } catch (_) {}
              
              if (!alreadySeeded) {
                console.log('📥 Seeding Central Discovery Queue from JD icp_requirements:', results.search_gen_response.icp_requirements.length);
                await centralDiscoveryQueue.addItemsFromICP(results.search_gen_response.icp_requirements, 'jd');
              } else {
                console.log('⏭️ Skipping JD seeding – items already seeded from JD for this session');
              }
            } else {
              console.log('📥 JD seeding skipped:', { isJD: aiOptions?.intakeType === 'jd', hasReqs: !!results?.search_gen_response?.icp_requirements?.length });
            }
          } catch (seedErr) {
            console.error('❌ Error seeding Central Discovery Queue from JD response:', seedErr);
          }
        }
        
        setMatchResults(results);
        setMatchesLoading(false);
  
        const to_match = [
          'cmetrics-female',
          'cmetrics-male',
          'cmetrics-african american',
          'cmetrics-south asian',
          'cmetrics-middle eastern',
          'cmetrics-caucasian',
          'cmetrics-east asian',
          'cmetrics-caucasian',
          'cmetrics-hispanic'
        ];
  
        const to_match_reqs = [
          {
            section_type: 'Must Have',
            section_name: 'ATS count',
            section_id: 'ats_to_match',
            bool_str: '(ats_info.activity.type:ats )'
          },
          {
            section_type: 'Must Have',
            section_name: 'ATS count',
            section_id: 'crm_to_match',
            bool_str: '(ats_info.activity.type: !ats)'
          },
          {
            section_type: 'Must Have',
            section_name: 'Alumni',
            section_id: 'alumni_to_match',
            bool_str: `(workexp.past.company.domain:findem.ai 
                      OR workexp.past.company.domain:findemresearch.com 
                      OR workexp.past.company.domain:findem-research.com) 
                      AND (workexp.current.company.domain:!findem.ai 
                      AND workexp.current.company.domain:!findemresearch.com 
                      AND workexp.current.company.domain:!findem-research.com)`
          },
          {
            section_type: 'Must Have',
            section_name: 'Current Employees',
            section_id: 'current_employees_to_match',
            bool_str: `(workexp.current.company.domain:findem.ai 
                      OR workexp.current.company.domain:findemresearch.com 
                      OR workexp.current.company.domain:findem-research.com)`
          }
        ];
  
        try {
          // Run Multi Matches
          const multiMatchResults = await runMultiMatches(
            results.search_gen_response.icp_requirements,
            to_match,
            []
          );
          setMatchCount(multiMatchResults);
          
          // If matchCount is greater than 10, run matches analyze with current org option
          let opt = {run_on_self_org: false}
          if (multiMatchResults && Object.values(multiMatchResults).some(count => count > 10)) {
            opt.run_on_self_org = true;
          }
          try {
            // IMPORTANT CHANGE: Always use the requirements from matchResults
            // This ensures we're using exactly what's displayed in IntakeSummary
            const requirements = results.search_gen_response.icp_requirements;
            
            // Log the requirements being used for matches analyze
            console.log('useMatchResults - Using requirements for matches analyze:', requirements.map(req => ({
              section_name: req.section_name,
              bool_str: req.bool_str,
              section_id: req.section_id
            })));
            
            // Run matches analyze
            const analyzeResults = await runMatchesAnalyze(requirements, opt);
            setMatchesAnalyze(analyzeResults);
            resolve(); // Resolve the promise when everything is done
          } catch (analyzeErr) {
            console.error('Error running matches analyze:', analyzeErr);
            setMatchesAnalyze(null);
            resolve(); // Still resolve to ensure .finally() is called
          }
        } catch (err) {
          console.error('Error fetching multi-match results:', err);
          setMatchCount(null); // Reset in case of error
          resolve(); // Still resolve to ensure .finally() is called
        }
      } catch (err) {
        console.error('Error fetching AI match results:', err);
        // Ensure JD in-flight gate is cleared on error
        if (aiOptions?.intakeType === 'jd' && typeof window !== 'undefined') {
          window.__jdInFlight = false;
        }
        setMatchResults(null); // Reset in case of error
        setMatchesLoading(false);
        resolve(); // Still resolve to ensure .finally() is called
      }
    });
  };

  /**
   * Save the current search to Findem
   * @param {string} sessionName - Name of the session
   */
  const saveOpenSearch = async (sessionName, saveCurrentSession) => {
    if (!matchResults) {
      console.error('No match results to save');
      return;
    }
    
    try {
      // IMPORTANT CHANGE: Always use the requirements from matchResults
      // This ensures we're using exactly what's displayed in IntakeSummary
      const requirements = matchResults.search_gen_response.icp_requirements;
      
      // Log the requirements being used for saving search
      console.log('useMatchResults.saveOpenSearch - Using requirements:', requirements.map(req => ({
        section_name: req.section_name,
        bool_str: req.bool_str,
        section_id: req.section_id
      })));
      
      const icp = await saveIcp(
        sessionName,
        sessionSearchId,
        requirements
      );
      setSessionSearchId(icp.ref);
      
      // If saveCurrentSession function is provided, call it with the session name and search ID
      if (saveCurrentSession) {
        saveCurrentSession(sessionName, icp.ref);
      }
      
      // Open the search in a new tab
      window.open(`https://app-next.findem.ai/searches/${icp.ref}`, '_blank');
    } catch (error) {
      console.error('Error saving search:', error);
    }
  };

  /**
   * Open the current search in Findem without saving
   */
  const openFindemSearch = () => {
    if (!matchResults || !sessionSearchId) {
      console.error('No match results or session search ID to open');
      return;
    }
    
    // Open the search in a new tab
    window.open(`https://app-next.findem.ai/searches/${sessionSearchId}`, '_blank');
  };

  /**
   * Reset match results state
   */
  const resetMatchResults = () => {
    setMatchResults(null);
    setMatchCount(null);
    setMatchesAnalyze(null);
    setSessionSearchId(null); // Reset session search ID when clearing results
    setIsLoading(false); // Reset loading state as well
  };

  return {
    matchResults,
    matchCount,
    matchesLoading,
    matchesAnalyze,
    sessionSearchId,
    isLoading,
    handleRefreshResults,
    handleRefreshResultsFromQueue,
    saveOpenSearch,
    resetMatchResults,
    setSessionSearchId,
    openFindemSearch
  };
};

export default useMatchResults;
